import React, { useState } from "react";
import { Layout, Menu, Divider, Typography } from "antd";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import tandemlyIcon from "../assets/icons/tandemly-icon.png";
import { SUPER_ADMIN } from "../constants";
import {
  LayoutDashboard,
  Users,
  MessageSquare,
  Heart,
  Star,
  Mic,
  Shield,
  Mail,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  Settings,
  AlertTriangle,
  Crown,
  UserPlus,
  Globe,
  UserCircle,
} from "lucide-react";

const { Sider } = Layout;

const items = [
  {
    label: "Dashboard",
    key: "dashboard",
    icon: <LayoutDashboard size={18} />,
    link: "/",
  },
  {
    label: "Users",
    key: "users",
    icon: <Users size={18} />,
    link: "/users",
  },
  
  {
    label: "Questions",
    key: "questions",
    icon: <MessageSquare size={18} />,
    link: "/questions",
  },
  {
    label: "LifeStyle Questions",
    key: "liststyle-questions",
    icon: <Heart size={18} />,
    link: "/liststyle-questions",
  },
  {
    label: "Interest",
    key: "interest",
    icon: <Star size={18} />,
    link: "/interest",
  },
  {
    label: "Ethnicities",
    key: "ethnicities",
    icon: <UserCircle size={18} />,
    link: "/ethnicities",
  },
  {
    label: "Origin Countries",
    key: "origin-countries",
    icon: <Globe size={18} />,
    link: "/origin-countries",
  },
  {
    label: "Broadcast",
    key: "broadcast",
    icon: <Mic size={18} />,
    link: "/broadcast",
  },
  {
    label: "Events",
    key: "events",
    icon: <Calendar size={18} />,
    link: "/events",
  },
  {
    label: "User Verification",
    key: "user-verification",
    icon: <Shield size={18} />,
    link: "/user-verification",
  },
  {
    label: "Contact-us",
    key: "contact-us",
    icon: <Mail size={18} />,
    link: "/contact-us",
  },
  {
    label: "CMS",
    key: "cms",
    icon: <FileText size={18} />,
    link: "/pages",
  },
  {
    label: "Reports",
    key: "reports",
    icon: <AlertTriangle size={18} />,
    link: "/reports",
  },
  {
    label: "Referrals",
    key: "referred-users",
    icon: <UserPlus size={18} />,
    link: "/referred-users",
  },
  {
    label: "Transactions",
    key: "transactions",
    icon: <Crown size={18} />,
    link: "/transactions",
  },
  {
    label: "Global Test Ads",
    key: "global-test-ads",
    icon: <Settings size={18} />,
    link: "/global-test-ads",
  },
  // {
  //   label: "Subscription Analytics",
  //   key: "subscription-analytics",
  //   icon: <LayoutDashboard size={18} />,
  //   link: "/subscription-analytics",
  // },
  // {
  //     label: 'Settings',
  //     key: 'settings',
  //     icon: <Settings size={18} />,
  //     link: '/settings'
  // },
];

const courtItems = [
  {
    label: "Dashboard",
    key: "dashboard",
    icon: <LayoutDashboard size={18} />,
    link: "/",
  },
  // {
  //     label: 'Court Bookings',
  //     key: 'bookings',
  //     icon: <MessageSquare size={18} />,
  //     link: '/bookings'
  // }
];

function activate() {
  const pathname = window.location.pathname;

  if (pathname !== "/") {
    let key = pathname.replace("/", "");

    const pathSegments = key.split("/");

    // const firstSegment = pathSegments.find(segment => segment !== '');

    return pathSegments[0];
  }

  return "dashboard";
}

const { Text } = Typography;

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const role = localStorage.getItem("role");

  const toggleCollapsed = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    // Dispatch event to notify other components
    window.dispatchEvent(
      new CustomEvent("sidebarCollapse", {
        detail: { collapsed: newCollapsed },
      }),
    );
  };

  const getActiveKey = () => {
    const pathname = location.pathname;
    if (pathname === "/") return "dashboard";

    const pathSegments = pathname
      .split("/")
      .filter((segment) => segment !== "");
    return pathSegments[0] || "dashboard";
  };

  const menuItems = (role === SUPER_ADMIN ? items : courtItems).map((item) => ({
    key: item.key,
    icon: (
      <motion.div
        whileHover={{ scale: 1.1 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.icon}
      </motion.div>
    ),
    label: collapsed ? (
      <Link
        to={item.link}
        style={{
          textDecoration: "none",
          fontWeight: 700,
          transition: "var(--transition-fast)",
        }}
      >
        {item.label}
      </Link>
    ) : (
      <Link
        to={item.link}
        style={{
          textDecoration: "none",
          fontWeight: 700,
          transition: "var(--transition-fast)",
        }}
      >
        {item.label}
      </Link>
    ),
  }));

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      width={collapsed ? 80 : 240}
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        background: "var(--sidebar-bg)",
        backdropFilter: "blur(20px)",
        borderRight: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "var(--shadow-card)",
        transition: "var(--transition-normal)",
        zIndex: 100,
      }}
      trigger={null}
    >
      {/* Logo Section */}
      <div
        className={`sidebar-brand ${collapsed ? "sidebar-brand--collapsed" : ""}`}
      >
        <Link to="/" className="sidebar-brand__link" aria-label="Tandemly home">
          <motion.img
            src={tandemlyIcon}
            alt=""
            className="sidebar-brand__logo"
            whileHover={{ scale: 1.04 }}
            transition={{ type: "spring", stiffness: 320, damping: 18 }}
          />
          {!collapsed && (
            <span className="sidebar-brand__name">
              tandemly
              <span className="sidebar-brand__heart" aria-hidden="true">
                ♥
              </span>
            </span>
          )}
        </Link>
      </div>

      {/* Navigation Menu */}
      <div
        style={{
          overflow: "auto",
          maxHeight: "calc(100vh - 140px)",
          marginBottom: "var(--spacing-xl)",
        }}
      >
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[getActiveKey()]}
          style={{
            background: "transparent",
            color: "var(--text-white)",
            fontSize: "0.875rem",
            border: "none",
            marginTop: "var(--spacing-md)",
            fontWeight: 700,
            paddingBottom: "var(--spacing-lg)",
          }}
          items={menuItems}
        />
      </div>

      {/* Collapse Trigger */}
      <div
        style={{
          position: "absolute",
          bottom: "var(--spacing-lg)",
          left: "50%",
          transform: "translateX(-50%)",
          cursor: "pointer",
          width: "32px",
          height: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "var(--transition-fast)",
        }}
        onClick={toggleCollapsed}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateX(-50%) scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateX(-50%) scale(1)";
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "#ffffff",
            border: "1px solid var(--border-light)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-gray)",
            transition: "var(--transition-fast)",
          }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </div>
      </div>
    </Sider>
  );
};

export default Sidebar;
